import { formatarCnpj } from '@precad/compartilhado/validacao/cnpj'
import { formatarCep } from '@precad/compartilhado/validacao/cep'
import { formatarCpf } from '@precad/compartilhado/validacao/cpf'
import { formatarTelefone } from '@precad/compartilhado/validacao/telefone'
import type { DocumentoEnviado, Socio, TipoCliente } from '../tipos'

/**
 * Etapa 7 — revisão e envio.
 *
 * Última chance de corrigir antes de a solicitação entrar na esteira. Cada
 * bloco leva um atalho para a etapa que o originou: mandar a pessoa "voltar
 * quatro vezes" para trocar um bairro é o tipo de atrito que faz desistir no
 * fim, depois de todo o trabalho.
 *
 * A completude é reconferida no SERVIDOR — esta tela é cortesia, não porteiro.
 */

interface Props {
  dados: Record<string, string>
  tiposCliente: TipoCliente[]
  socios: Socio[]
  documentos: DocumentoEnviado[]
  aceites: string[]
  aoIr: (etapa: number) => void
  aoVoltar: () => void
  aoEnviar: () => void
  ocupado: boolean
}

export function Revisao({
  dados,
  tiposCliente,
  socios,
  documentos,
  aceites,
  aoIr,
  aoVoltar,
  aoEnviar,
  ocupado,
}: Props) {
  const tipo = tiposCliente.find((t) => String(t.id) === dados.tipoClienteId)

  return (
    <div>
      <p className="chamada">
        Confira antes de enviar. Depois do envio, alterações só pela área de acompanhamento.
      </p>

      <Bloco titulo="Empresa" aoEditar={() => aoIr(2)}>
        <Item rotulo="CNPJ" valor={dados.cnpj ? formatarCnpj(dados.cnpj) : '—'} />
        <Item rotulo="Razão social" valor={dados.razaoSocial} />
        <Item rotulo="Nome fantasia" valor={dados.nomeFantasia} />
        <Item rotulo="Inscrição estadual" valor={dados.inscricaoEstadual || 'Isento'} />
        <Item rotulo="Tipo de cliente" valor={tipo?.nome ?? '—'} />
        {dados.tipoClienteOutro ? <Item rotulo="Qual" valor={dados.tipoClienteOutro} /> : null}
        <Item
          rotulo="Filiais"
          valor={dados.possuiFiliais === '1' ? `Sim — ${dados.quantidadeFiliais || '?'}` : 'Não'}
        />
      </Bloco>

      <Bloco titulo="Endereço" aoEditar={() => aoIr(2)}>
        <Item rotulo="CEP" valor={dados.cep ? formatarCep(dados.cep) : '—'} />
        <Item
          rotulo="Logradouro"
          valor={[dados.endereco, dados.numeroEndereco, dados.complemento].filter(Boolean).join(', ')}
        />
        <Item rotulo="Bairro" valor={dados.bairro} />
        <Item rotulo="Cidade" valor={[dados.cidade, dados.uf].filter(Boolean).join(' — ')} />
      </Bloco>

      <Bloco titulo="Contato" aoEditar={() => aoIr(3)}>
        <Item rotulo="Telefone" valor={dados.telefone ? formatarTelefone(dados.telefone) : '—'} />
        <Item rotulo="WhatsApp" valor={dados.whatsapp ? formatarTelefone(dados.whatsapp) : '—'} />
        <Item rotulo="E-mail" valor={dados.email} />
      </Bloco>

      <Bloco titulo={`Quadro societário (${socios.length})`} aoEditar={() => aoIr(4)}>
        {socios.map((s) => (
          <Item key={s.id ?? s.cpf} rotulo={s.nome} valor={s.cpf ? formatarCpf(s.cpf) : '—'} />
        ))}
      </Bloco>

      <Bloco titulo={`Documentos (${documentos.length})`} aoEditar={() => aoIr(5)}>
        {documentos.length ? (
          documentos.map((d) => <Item key={d.id} rotulo={d.nomeArquivo} valor="Recebido" />)
        ) : (
          <Item rotulo="Nenhum documento enviado" valor="—" />
        )}
      </Bloco>

      <Bloco titulo="Aceites" aoEditar={() => aoIr(6)}>
        {aceites.length ? (
          aceites.map((c) => <Item key={c} rotulo={c} valor="Aceito" />)
        ) : (
          <Item rotulo="Nenhum aceite registrado" valor="—" />
        )}
      </Bloco>

      <div className="rodape">
        <button type="button" className="botao botao--secundario" onClick={aoVoltar}>
          Voltar
        </button>
        <span className="rodape__espaco" />
        <button type="button" className="botao botao--principal" onClick={aoEnviar} aria-disabled={ocupado}>
          {ocupado ? 'Enviando…' : 'Enviar pré-cadastro'}
        </button>
      </div>
    </div>
  )
}

function Bloco({
  titulo,
  aoEditar,
  children,
}: {
  titulo: string
  aoEditar: () => void
  children: React.ReactNode
}) {
  return (
    <section className="bloco">
      <h3 className="bloco__titulo">
        {titulo}
        <button type="button" className="bloco__editar" onClick={aoEditar}>
          Editar
        </button>
      </h3>
      <dl className="resumo">{children}</dl>
    </section>
  )
}

function Item({ rotulo, valor }: { rotulo: string; valor?: string }) {
  return (
    <>
      <dt>{rotulo}</dt>
      <dd>{valor || '—'}</dd>
    </>
  )
}
